import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { JournalVoucher, AccountsPayment } from '../types';

export function useJournalVouchers(filters: { status?: string; search?: string } = {}) {
  return useQuery({
    queryKey: ['accounts', 'journal-vouchers', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      const { data } = await api.get<{ data: JournalVoucher[] } | JournalVoucher[]>(`/api/accounts/journal-vouchers?${params}`);
      return Array.isArray(data) ? data : data.data;
    },
  });
}

export function useJournalVoucherDetail(id: string | null) {
  return useQuery({
    queryKey: ['accounts', 'journal-vouchers', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await api.get(`/api/accounts/journal-vouchers/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useUpdateJournalVoucher() {
  const queryClient = useQueryClient();
  return useMutation({ mutationFn: async ({ id, payload }: { id: string; payload: any }) => (await api.put(`/api/accounts/journal-vouchers/${id}`, payload)).data, onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'journal-vouchers'] }) });
}

export function useJournalAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action, payload }: { id: string; action: 'submit' | 'approve' | 'reject' | 'post' | 'reverse' | 'delete'; payload?: any }) => {
      if (action === 'delete') return (await api.delete(`/api/accounts/journal-vouchers/${id}`, { data: payload })).data;
      return (await api.post(`/api/accounts/journal-vouchers/${id}/${action}`, payload || {}, action === 'post' || action === 'reverse' ? { headers: { 'X-Idempotency-Key': crypto.randomUUID() } } : undefined)).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'journal-vouchers'] }),
  });
}

export function useCreateJournalVoucher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/api/accounts/journal-vouchers', payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'journal-vouchers'] }),
  });
}

export function useSalesInvoices() {
  return useQuery({
    queryKey: ['accounts', 'sales-invoices'],
    queryFn: async () => {
      const { data } = await api.get('/api/accounts/sales-invoices');
      // Handle both paginated {data, pagination} and plain array responses
      return Array.isArray(data) ? data : (data.data || []);
    },
  });
}

export function useCreateSalesInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/api/accounts/sales-invoices', payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] }),
  });
}

export function usePurchaseBills() {
  return useQuery({
    queryKey: ['accounts', 'purchase-bills'],
    queryFn: async () => {
      const { data } = await api.get('/api/accounts/purchase-bills');
      return Array.isArray(data) ? data : (data.data || []);
    },
  });
}

export function useCreatePurchaseBill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/api/accounts/purchase-bills', payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'purchase-bills'] }),
  });
}

export function usePayments() {
  return useQuery({
    queryKey: ['accounts', 'payments'],
    queryFn: async () => {
      const { data } = await api.get<AccountsPayment[]>('/api/accounts/payments');
      return data;
    },
  });
}

export function useCreatePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/api/accounts/payments', payload, { headers: { 'X-Idempotency-Key': crypto.randomUUID() } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['accounts', 'purchase-bills'] });
    },
  });
}
